/**
 * LegacyQuestionStep Component
 * Extracted from DeviceSpecifications.tsx
 * Handles legacy question flow UI (when questions.length === 0)
 */

import React, { useState } from 'react';
import {
  Wifi, Signal, BatteryFull, BatteryLow, Battery,
  CheckCircle, AlertTriangle, X, Star, StarHalf,
  AlertCircle, ShieldCheck, Sparkles, Lightbulb
} from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { Question } from './types';

// Icon Components
const WifiIcon = () => (<Wifi size={20} strokeWidth={1.75} />);
const CellularIcon = () => (<Signal size={20} strokeWidth={1.75} />);
const BatteryGoodIcon = () => (<BatteryFull size={20} strokeWidth={1.75} />);
const BatteryLowIcon = () => (<BatteryLow size={20} strokeWidth={1.75} />);
const BatteryUnknownIcon = () => (<Battery size={20} strokeWidth={1.75} />);
const CheckCircleIcon = () => (<CheckCircle size={20} strokeWidth={1.75} />);
const WarningTriangleIcon = () => (<AlertTriangle size={20} strokeWidth={1.75} />);
const XCircleIcon = () => (<X size={20} strokeWidth={1.75} />);
const PhoneSparkleIcon = () => (<Sparkles size={20} strokeWidth={1.75} />);
const PhoneScratchLightIcon = () => (<StarHalf size={20} strokeWidth={1.75} />);
const PhoneScratchIcon = () => (<AlertCircle size={20} strokeWidth={1.75} />);
const PhoneCrackMajorIcon = () => (<X size={20} strokeWidth={1.75} />);
const PhoneOutlineIcon = () => (<ShieldCheck size={20} strokeWidth={1.75} />);
const PhoneScuffIcon = () => (<Star size={20} strokeWidth={1.75} />);
const PhoneWearIcon = () => (<AlertTriangle size={20} strokeWidth={1.75} />);

// Styled Components (same as QuestionStep)
const phoneSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-80px);
  }
  to {
    opacity: 1;
    transform: translateX(-40px);
  }
`;

const phoneSlideInWide = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100px);
  }
  to {
    opacity: 1;
    transform: translateX(-64px);
  }
`;

const QuestionContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
}) <{ visible: boolean }>`
  display: ${props => props.visible ? 'block' : 'none'};
`;

const QuestionTwoCol = styled.div`
  display: grid;
  grid-template-columns: 160px 1px 1fr;
  gap: 24px;
  align-items: start;
  min-width: 100%;
  width: 100%;
  
  > div:last-child {
    min-width: 600px;
    width: 100%;
  }
  
  @media (max-width: 1024px) {
    grid-template-columns: 150px 1px 1fr;
    > div:last-child {
      min-width: 500px;
    }
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    > div:last-child {
      min-width: 100%;
    }
  }
`;

const HelperRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 24px;
  align-items: start;
  margin-top: 0;
  @media (max-width: 1024px) {
    grid-template-columns: 150px 1fr;
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const IllustrationPanel = styled.div`
  display: none;
  @media (min-width: 861px) {
    display: block;
  }
  margin: 0;
`;

const ColumnDivider = styled.div`
  display: none;
  @media (min-width: 861px) {
    display: block;
    align-self: stretch;
    justify-self: center;
    width: 1px;
    background: linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.04) 100%);
    border-radius: 1px;
    transform: translateX(-16px);
    @media (min-width: 1280px) {
      transform: translateX(-22px);
    }
  }
`;

const IllustrationCard = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  height: 380px;
  position: relative;
  overflow: hidden;
  display: block;
  margin-left: -1.25rem;
  box-shadow: none;
`;

const PhoneIllustrationFrame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 0;

  img, .device-image {
    height: 100%;
    width: auto;
    max-width: none;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 12px 20px rgba(0,0,0,0.12));
    transform: translateX(-40px);
    will-change: transform, opacity;
    animation: ${phoneSlideIn} 1200ms cubic-bezier(0.16, 1, 0.3, 1) 100ms both;
  }

  @media (min-width: 1280px) {
    img, .device-image {
      height: 100%;
      transform: translateX(-64px);
      animation: ${phoneSlideInWide} 1400ms cubic-bezier(0.16, 1, 0.3, 1) 120ms both;
    }
  }
`;

const OptionsGrid = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'columns',
}) <{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 3}, minmax(180px, 1fr));
  gap: 0.875rem;
  margin-bottom: 0.2rem;
  justify-content: stretch;
  width: 100%;
  
  ${props => props.columns === 2 && `
    max-width: 600px;
    margin-left: 0;
    margin-right: auto;
  `}
  ${props => props.columns === 3 && `
    max-width: 900px;
    margin-left: 0;
    margin-right: auto;
  `}
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(${props => Math.min(2, (props.columns || 3))}, minmax(180px, 1fr));
    ${props => props.columns === 2 && `
      max-width: 500px;
    `}
    ${props => props.columns === 3 && `
      max-width: 100%;
    `}
  }
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.625rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    margin-bottom: 0.15rem;
  }
`;

const OptionCard = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'variant',
}) <{ selected?: boolean; variant?: 'storage' | 'network' | 'battery' | 'condition' | 'functionality' | 'screen' | 'housing' }>`
  background: #ffffff;
  color: #111827;
  border: 1px solid #e2e8f0;
  padding: 1rem 0.75rem;
  border-radius: 18px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  min-height: 110px;
  position: relative;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  transform: translateY(0);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
    border-color: #cbd5e1;
  }

  &:focus-visible {
    outline: 2px solid #0f172a;
    outline-offset: 2px;
  }

  ${props => props.selected && `
    border-color: #0f172a;
    border-width: 2px;
    background: #f8fafc;
    box-shadow: 0 6px 14px rgba(0,0,0,0.05);
    padding: calc(1rem - 1px) calc(0.75rem - 1px); /* Compensate for border width */
  `}

  @media (max-width: 480px) {
    min-height: 96px;
    border-radius: 14px;
    padding: 0.875rem 0.625rem;
    gap: 0.5rem;
    
    ${props => props.selected && `
      padding: calc(0.875rem - 1px) calc(0.625rem - 1px);
    `}
  }

  .dark & {
    background: #151921;
    color: #e5e7eb;
    border: 1px solid #262b36;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
    
    &:hover {
      border-color: #334155;
      background: #1e293b;
      box-shadow: 0 8px 24px rgba(0,0,0,0.3);
    }
    
    ${props => props.selected && `
      border-color: #e2e8f0;
      background: #1e293b;
      box-shadow: 0 8px 24px rgba(0,0,0,0.3);
    `}
  }
`;

const OptionIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
}) <{ selected?: boolean }>`
  margin-bottom: 0.25rem;
  position: relative;
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #f1f5f9;
  border: 1px solid transparent; 
  transition: all 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
    color: ${props => (props.selected ? '#0f172a' : '#64748b')};
    stroke-width: 1.75;
    transition: all 0.2s ease;
  }

  ${props => props.selected && `
    background: #1f2937;
    color: #ffffff;
    
    svg {
      color: #ffffff;
    }
  `}

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    svg { width: 20px; height: 20px; }
  }

  ${OptionCard}:hover & {
    background: #e2e8f0;
    transform: scale(1.05);
  }

  .dark & {
    background: #1e293b;
    svg { color: #94a3b8; }
    
    ${props => props.selected && `
      background: #e2e8f0;
      svg { color: #0f172a; }
    `}
  }
  
  .dark ${OptionCard}:hover & {
    background: #334155;
  }
`;

const OptionText = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  letter-spacing: -0.015em;

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }

  .dark & {
    color: #f1f5f9;
  }
`;

const OptionDescription = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
  margin-top: -0.125rem;
  max-width: 90%;

  @media (max-width: 480px) {
    font-size: 0.75rem;
  }

  .dark & {
    color: #94a3b8;
  }
`;

const HelperText = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 20px;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s ease;

  @media (max-width: 480px) {
    margin-top: 1rem;
    padding: 1rem 1.25rem;
    border-radius: 16px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  }

  h4 {
    font-size: 1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;

    svg {
      width: 20px;
      height: 20px;
      color: #3b82f6;
      padding: 4px;
      border-radius: 50%;
      background: rgba(59, 130, 246, 0.1);
    }
  }

  p {
    font-size: 0.95rem;
    color: #475569;
    margin: 0 auto;
    line-height: 1.6;
    max-width: 95%;
  }

  @media (max-width: 480px) {
    h4 {
      font-size: 0.9rem;
      margin: 0 0 0.35rem 0;
    }
    h4 svg {
      width: 16px;
      height: 16px;
      padding: 3px;
    }
    p {
      font-size: 0.85rem;
      line-height: 1.5;
    }
  }

  .dark & {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border-color: #334155;
    box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.5);
  }
  .dark & h4 { color: #f1f5f9; }
  .dark & h4 svg { 
    color: #60a5fa; 
    background: rgba(96, 165, 250, 0.15);
  }
  .dark & p { color: #cbd5e1; }
`;

const TipIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fde68a;
  color: #d97706;
  flex-shrink: 0;
  box-shadow: 0 2px 5px rgba(245, 158, 11, 0.15);

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    min-width: 36px;
    border-radius: 10px;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .dark & {
    background: linear-gradient(135deg, #78350f 0%, #451a03 100%);
    border-color: #92400e;
    color: #fcd34d;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }
`;

// Enhanced device image component with fallback system
const DeviceImageWithFallback: React.FC<{
  modelName: string;
  alt: string;
  className?: string;
  imageSrc?: string; // Database image URL (takes precedence over generated paths)
}> = ({ modelName, alt, className, imageSrc }) => {
  const [currentImagePath, setCurrentImagePath] = useState<string>('');
  const [imageError, setImageError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const getImageVariants = (modelName: string): string[] => {
    const name = modelName.toLowerCase().replace(/\s+/g, '-');
    const baseVariants: string[] = [];

    if (name.includes('iphone')) {
      const iphoneModel = name.replace('iphone-', '').replace(/[^a-z0-9-]/g, '-');
      // PascalCase variant (e.g., "17-air" → "Apple-Iphone-17-Air-PNG.png")
      const pascalModel = iphoneModel.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('-');
      baseVariants.push(
        `/devices/iphone-${iphoneModel}.png`,
        `/devices/Apple-Iphone-${pascalModel}-PNG.png`,
        `/devices/iphone-${iphoneModel}-hd.png`,
        `/devices/iphone-${iphoneModel}-high.png`,
        `/devices/iphone-${iphoneModel}-medium-png.png`,
      );
      // Generation-based fallback: for new models without images, try previous generation
      const genMatch = iphoneModel.match(/^(\d+)/);
      if (genMatch) {
        const prevGen = parseInt(genMatch[1]) - 1;
        if (prevGen >= 7) {
          baseVariants.push(
            `/devices/iphone-${prevGen}-pro-max.png`,
            `/devices/iphone-${prevGen}-pro.png`,
            `/devices/iphone-${prevGen}.png`,
          );
        }
      }
      baseVariants.push(
        `/devices/iphone-medium-png.png`,
        `/devices/iphone.png`
      );
    } else if (name.includes('ipad')) {
      const ipadModel = name.replace('ipad-', '').replace(/[^a-z0-9-]/g, '-');
      if (ipadModel.includes('pro-13-7') || ipadModel.includes('pro-13')) {
        baseVariants.push(
          `/iPad/ipad-pro-13inch-7de-gen-medium-png.png`,
          `/devices/ipad-pro-13-7.png`
        );
      } else if (ipadModel.includes('pro-12-9-6') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('6'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-6de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-6.png`
        );
      } else if (ipadModel.includes('pro-12-9-5') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('5'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-5de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-5.png`
        );
      } else if (ipadModel.includes('pro-12-9-4') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('4'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-4de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-4.png`
        );
      } else {
        baseVariants.push(
          `/devices/ipad-${ipadModel}.png`,
          `/devices/ipad-${ipadModel}-hd.png`,
          `/devices/ipad.png`
        );
      }
    } else if (name.includes('macbook')) {
      const macbookModel = name.replace('macbook-', '').replace(/[^a-z0-9-]/g, '-');
      baseVariants.push(
        `/devices/macbook-${macbookModel}.png`,
        `/devices/macbook-${macbookModel}-hd.png`,
        `/devices/macbook.png`
      );
    } else if (name.includes('samsung') || name.includes('galaxy')) {
      const samsungModel = name.replace(/^(samsung-|galaxy-)/, '').replace(/[^a-z0-9-]/g, '-');
      baseVariants.push(
        `/devices/galaxy-${samsungModel}.png`,
        `/devices/galaxy-${samsungModel}-hd.png`,
        `/devices/galaxy.png`
      );
    } else {
      baseVariants.push(`/devices/${name}.png`, `/devices/device.png`);
    }

    return baseVariants;
  };

  const loadIdRef = React.useRef<number>(0);

  React.useEffect(() => {
    if (!modelName) {
      setIsLoading(false);
      return;
    }

    const thisLoadId = ++loadIdRef.current;

    // Build variants array - prioritize imageSrc from database if provided
    const fallbackVariants = getImageVariants(modelName);
    const variants = imageSrc && imageSrc !== '/devices/default.png'
      ? [imageSrc, ...fallbackVariants]
      : fallbackVariants;

    let variantIndex = 0;
    let cancelled = false;

    const tryNextVariant = () => {
      if (cancelled || loadIdRef.current !== thisLoadId) return;
      if (variantIndex >= variants.length) {
        if (!currentImagePath) setImageError(true);
        setIsLoading(false);
        return;
      }

      const img = new Image();
      const currentVariant = variants[variantIndex];

      img.onload = () => {
        if (cancelled || loadIdRef.current !== thisLoadId) return;
        setCurrentImagePath(currentVariant);
        setImageError(false);
        setIsLoading(false);
      };

      img.onerror = () => {
        if (cancelled || loadIdRef.current !== thisLoadId) return;
        variantIndex++;
        tryNextVariant();
      };

      img.src = encodeURI(currentVariant);
    };

    if (!currentImagePath) {
      setIsLoading(true);
    }
    setImageError(false);
    tryNextVariant();

    return () => { cancelled = true; };
  }, [modelName, imageSrc]);

  if (!modelName) return null;

  if (imageError || !currentImagePath) {
    return (
      <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '0.875rem' }}>
        {alt}
      </div>
    );
  }

  return (
    <img
      src={encodeURI(currentImagePath)}
      alt={alt}
      className={className}
      style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
      loading="lazy"
    />
  );
};

// Component Props
interface LegacyQuestionStepProps {
  currentQuestion: Question;
  isIPhone: boolean;
  shouldShowNetworkQuestion: boolean;
  shouldShowBatteryQuestion: boolean;

  // Legacy selected values
  selectedCondition: string | null;
  selectedNetworkType: string | null;
  selectedBattery: string | null;
  selectedFunctionality: string | null;
  selectedScreenCondition: string | null;
  selectedHousingCondition: string | null;
  selectedBatteryCapacity: string | null;

  // Callbacks
  onSelectCondition: (value: string) => void;
  onSelectNetworkType: (value: string) => void;
  onSelectBattery: (value: string) => void;
  onSelectFunctionality: (value: string) => void;
  onSelectScreenCondition: (value: string) => void;
  onSelectHousingCondition: (value: string) => void;
  onSelectBatteryCapacity: (value: string) => void;

  // General
  modelName: string;
  imageSrc?: string; // Database image URL for device illustration
  t: (key: string) => string;
  visible: boolean;
  defectsImageError: boolean;
  onDefectsImageError: () => void;
}

// Main Component
const LegacyQuestionStep: React.FC<LegacyQuestionStepProps> = ({
  currentQuestion,
  isIPhone,
  shouldShowNetworkQuestion,
  shouldShowBatteryQuestion,
  selectedCondition,
  selectedNetworkType,
  selectedBattery,
  selectedFunctionality,
  selectedScreenCondition,
  selectedHousingCondition,
  selectedBatteryCapacity,
  onSelectCondition,
  onSelectNetworkType,
  onSelectBattery,
  onSelectFunctionality,
  onSelectScreenCondition,
  onSelectHousingCondition,
  onSelectBatteryCapacity,
  modelName,
  imageSrc,
  t,
  visible,
  defectsImageError,
  onDefectsImageError,
}) => {
  // Build option arrays (these depend on t function)
  const networkOptions = [
    { value: 'wifi', icon: (<WifiIcon />), description: t('wifiOnly') },
    { value: 'cellular', icon: (<CellularIcon />), description: t('wifiCellular') }
  ];

  const batteryOptions = [
    { value: 'above85', icon: (<BatteryGoodIcon />), description: t('batteryAbove85Desc') },
    { value: 'below85', icon: (<BatteryLowIcon />), description: t('batteryBelow85Desc') },
    { value: 'unknown', icon: (<BatteryUnknownIcon />), description: t('batteryUnknownDesc') }
  ];

  const conditionOptions: { grade: string; icon: JSX.Element; description: string }[] = [
    { grade: 'A', icon: (<CheckCircleIcon />), description: t('conditionExcellentDesc') },
    { grade: 'B', icon: (<WarningTriangleIcon />), description: t('conditionGoodDesc') },
    { grade: 'C', icon: (<WarningTriangleIcon />), description: t('conditionFairDesc') },
    { grade: 'D', icon: (<XCircleIcon />), description: t('conditionPoorDesc') }
  ];

  const functionalityOptions = [
    { value: 'works', icon: (<CheckCircleIcon />), description: t('worksNormally') },
    { value: 'issues', icon: (<WarningTriangleIcon />), description: t('hasIssues') }
  ];

  const screenConditionOptions = [
    { value: 'perfect', icon: (<PhoneSparkleIcon />), description: t('screenPerfect') },
    { value: 'light', icon: (<PhoneScratchLightIcon />), description: t('screenLightScratches') },
    { value: 'scratches', icon: (<PhoneScratchIcon />), description: t('screenScratches') },
    { value: 'major', icon: (<PhoneCrackMajorIcon />), description: t('screenMajorDamage') }
  ];

  const housingConditionOptions = [
    { value: 'perfect', icon: (<PhoneOutlineIcon />), description: t('housingPerfect') },
    { value: 'light', icon: (<PhoneScuffIcon />), description: t('housingLightScratches') },
    { value: 'wear', icon: (<PhoneWearIcon />), description: t('housingWear') },
    { value: 'major', icon: (<PhoneCrackMajorIcon />), description: t('housingMajorDamage') }
  ];

  const batteryCapacityOptions = [
    { value: 'above85', icon: (<BatteryGoodIcon />), description: t('batteryAbove85Percent') },
    { value: 'below85', icon: (<BatteryLowIcon />), description: t('batteryBelow85Percent') },
    { value: 'unknown', icon: (<BatteryUnknownIcon />), description: t('batteryUnknownCapacity') },
    { value: 'full', icon: (<BatteryGoodIcon />), description: t('batteryFull100Percent') }
  ];

  if (!visible) return null;

  return (
    <>
      {/* Network Type Selection */}
      {shouldShowNetworkQuestion && (
        <QuestionContainer visible={currentQuestion === Question.NetworkType}>
          <QuestionTwoCol>
            <IllustrationPanel>
              <IllustrationCard>
                <PhoneIllustrationFrame key={currentQuestion === Question.NetworkType ? 'network-active' : 'network'}>
                  <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                </PhoneIllustrationFrame>
              </IllustrationCard>
            </IllustrationPanel>
            <ColumnDivider />
            <div>
              <OptionsGrid columns={2}>
                {networkOptions.map((option) => (
                  <OptionCard
                    key={option.value}
                    selected={selectedNetworkType === option.value}
                    onClick={() => onSelectNetworkType(option.value)}
                    variant="network"
                  >
                    <OptionIcon selected={selectedNetworkType === option.value}>{option.icon}</OptionIcon>
                    <OptionText>{option.value === 'wifi' ? 'WiFi' : t('wifiCellular')}</OptionText>
                    <OptionDescription>{option.description}</OptionDescription>
                  </OptionCard>
                ))}
              </OptionsGrid>
            </div>
          </QuestionTwoCol>

          <HelperRow>
            <div />
            <HelperText>
              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="M12 5v14"></path>
                </svg>
                Hoe controleer je de connectiviteit?
              </h4>
              <p style={{ marginTop: '4px' }}>{t('checkNetworkInstructions')}</p>
            </HelperText>
          </HelperRow>
        </QuestionContainer>
      )}

      {/* Battery Selection */}
      {shouldShowBatteryQuestion && (
        <QuestionContainer visible={currentQuestion === Question.Battery}>
          <QuestionTwoCol>
            <IllustrationPanel>
              <IllustrationCard>
                <PhoneIllustrationFrame key={currentQuestion === Question.Battery ? 'battery-active' : 'battery'}>
                  <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                </PhoneIllustrationFrame>
              </IllustrationCard>
            </IllustrationPanel>
            <ColumnDivider />
            <div>
              <OptionsGrid columns={3}>
                {batteryOptions.map((option) => (
                  <OptionCard
                    key={option.value}
                    selected={selectedBattery === option.value}
                    onClick={() => onSelectBattery(option.value)}
                    variant="battery"
                  >
                    <OptionIcon selected={selectedBattery === option.value}>{option.icon}</OptionIcon>
                    <OptionText>
                      {option.value === 'above85' ? t('batteryAbove85') :
                        option.value === 'below85' ? t('batteryBelow85') : t('batteryUnknown')}
                    </OptionText>
                    <OptionDescription>{option.description}</OptionDescription>
                  </OptionCard>
                ))}
              </OptionsGrid>
            </div>
          </QuestionTwoCol>

          <HelperRow>
            <div />
            <HelperText>
              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
                Hoe controleer je de batterij status?
              </h4>
              <p style={{ marginTop: '4px' }}>{t('checkBatteryInstructions')}</p>
            </HelperText>
          </HelperRow>
        </QuestionContainer>
      )}

      {/* Condition Selection */}
      {!isIPhone && (
        <QuestionContainer visible={currentQuestion === Question.Condition}>
          <QuestionTwoCol>
            <IllustrationPanel>
              <IllustrationCard>
                <PhoneIllustrationFrame key={currentQuestion === Question.Condition ? 'condition-active' : 'condition'}>
                  <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                </PhoneIllustrationFrame>
              </IllustrationCard>
            </IllustrationPanel>
            <ColumnDivider />
            <div>
              <OptionsGrid columns={2}>
                {conditionOptions.map((option) => (
                  <OptionCard
                    key={option.grade}
                    selected={selectedCondition === option.grade}
                    onClick={() => onSelectCondition(option.grade)}
                    variant="condition"
                  >
                    <OptionIcon selected={selectedCondition === option.grade}>{option.icon}</OptionIcon>
                    <OptionText>{t('grade')} {option.grade}</OptionText>
                    <OptionDescription>{option.description}</OptionDescription>
                  </OptionCard>
                ))}
              </OptionsGrid>
            </div>
          </QuestionTwoCol>

          <HelperRow>
            <div />
            <HelperText>
              <h4>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                Hoe bepaal je de uiterlijke staat?
              </h4>
              <p style={{ marginTop: '4px' }}>{t('conditionHonestAssessment')}</p>
            </HelperText>
          </HelperRow>
        </QuestionContainer>
      )}

      {/* iPhone Specific Questions - Legacy */}
      {isIPhone && (
        <>
          {/* Functionality Selection */}
          <QuestionContainer visible={currentQuestion === Question.Functionality}>
            <QuestionTwoCol>
              <IllustrationPanel>
                <IllustrationCard>
                  <PhoneIllustrationFrame key={currentQuestion === Question.Functionality ? 'functionality-active' : 'functionality'}>
                    <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                  </PhoneIllustrationFrame>
                </IllustrationCard>
              </IllustrationPanel>
              <ColumnDivider />
              <div>
                <OptionsGrid columns={2}>
                  {functionalityOptions.map((option) => (
                    <OptionCard
                      key={option.value}
                      selected={selectedFunctionality === option.value}
                      onClick={() => onSelectFunctionality(option.value)}
                      variant="functionality"
                    >
                      <OptionIcon selected={selectedFunctionality === option.value}>{option.icon}</OptionIcon>
                      <OptionText>{option.description}</OptionText>
                    </OptionCard>
                  ))}
                </OptionsGrid>
              </div>
            </QuestionTwoCol>

            <HelperRow>
              <div />
              <HelperText>
                <h4>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                  </svg>
                  Hoe controleer je de functionaliteit?
                </h4>
                <p style={{ marginTop: '4px' }}>{t('checkFunctionalityInstructions')}</p>
              </HelperText>
            </HelperRow>
          </QuestionContainer>

          {/* Screen Condition Selection */}
          <QuestionContainer visible={currentQuestion === Question.ScreenCondition}>
            <QuestionTwoCol>
              <IllustrationPanel>
                <IllustrationCard>
                  <PhoneIllustrationFrame key={currentQuestion === Question.ScreenCondition ? 'screen-active' : 'screen'}>
                    {!defectsImageError ? (
                      <img
                        src="/images/screen_defects_combined.avif"
                        alt={t('screenDefectsAlt')}
                        draggable={false}
                        loading="eager"
                        onError={onDefectsImageError}
                      />
                    ) : (
                      <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                    )}
                  </PhoneIllustrationFrame>
                </IllustrationCard>
              </IllustrationPanel>
              <ColumnDivider />
              <div>
                <OptionsGrid columns={3}>
                  {screenConditionOptions.map((option) => (
                    <OptionCard
                      key={option.value}
                      selected={selectedScreenCondition === option.value}
                      onClick={() => onSelectScreenCondition(option.value)}
                      variant="screen"
                    >
                      <OptionIcon selected={selectedScreenCondition === option.value}>{option.icon}</OptionIcon>
                      <OptionText>{option.description}</OptionText>
                    </OptionCard>
                  ))}
                </OptionsGrid>
              </div>
            </QuestionTwoCol>

            <HelperRow>
              <div />
              <HelperText>
                <h4>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                    <line x1="12" y1="18" x2="12.01" y2="18"></line>
                  </svg>
                  Hoe controleer je het scherm?
                </h4>
                <p style={{ marginTop: '4px' }}>{t('checkScreenInstructions')}</p>
              </HelperText>
            </HelperRow>
          </QuestionContainer>

          {/* Housing Condition Selection */}
          <QuestionContainer visible={currentQuestion === Question.HousingCondition}>
            <QuestionTwoCol>
              <IllustrationPanel>
                <IllustrationCard>
                  <PhoneIllustrationFrame key={currentQuestion === Question.HousingCondition ? 'housing-active' : 'housing'}>
                    <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                  </PhoneIllustrationFrame>
                </IllustrationCard>
              </IllustrationPanel>
              <ColumnDivider />
              <div>
                <OptionsGrid columns={3}>
                  {housingConditionOptions.map((option) => (
                    <OptionCard
                      key={option.value}
                      selected={selectedHousingCondition === option.value}
                      onClick={() => onSelectHousingCondition(option.value)}
                      variant="housing"
                    >
                      <OptionIcon selected={selectedHousingCondition === option.value}>{option.icon}</OptionIcon>
                      <OptionText>{option.description}</OptionText>
                    </OptionCard>
                  ))}
                </OptionsGrid>
              </div>
            </QuestionTwoCol>

            <HelperRow>
              <div />
              <HelperText>
                <h4>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  Hoe controleer je de behuizing?
                </h4>
                <p style={{ marginTop: '4px' }}>{t('checkHousingInstructions')}</p>
              </HelperText>
            </HelperRow>
          </QuestionContainer>

          {/* Battery Capacity Selection */}
          <QuestionContainer visible={currentQuestion === Question.BatteryCapacity}>
            <QuestionTwoCol>
              <IllustrationPanel>
                <IllustrationCard>
                  <PhoneIllustrationFrame key={currentQuestion === Question.BatteryCapacity ? 'batterycap-active' : 'batterycap'}>
                    <DeviceImageWithFallback modelName={modelName} alt={modelName} imageSrc={imageSrc} />
                  </PhoneIllustrationFrame>
                </IllustrationCard>
              </IllustrationPanel>
              <ColumnDivider />
              <div>
                <OptionsGrid columns={3}>
                  {batteryCapacityOptions.map((option) => (
                    <OptionCard
                      key={option.value}
                      selected={selectedBatteryCapacity === option.value}
                      onClick={() => onSelectBatteryCapacity(option.value)}
                      variant="battery"
                    >
                      <OptionIcon selected={selectedBatteryCapacity === option.value}>{option.icon}</OptionIcon>
                      <OptionText>{option.description}</OptionText>
                    </OptionCard>
                  ))}
                </OptionsGrid>
              </div>
            </QuestionTwoCol>

            <HelperRow>
              <div />
              <HelperText>
                <h4>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="6" width="18" height="12" rx="2" ry="2"></rect>
                    <line x1="23" y1="13" x2="23" y2="11"></line>
                  </svg>
                  Hoe controleer je de maximumcapaciteit?
                </h4>
                <p style={{ marginTop: '4px' }}>{t('checkBatteryCapacityInstructions')}</p>
              </HelperText>
            </HelperRow>
          </QuestionContainer>
        </>
      )}
    </>
  );
};

export default LegacyQuestionStep;

